import { useCallback, useMemo } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import {
  useSendOrgMemberInvitation,
  useSuspendOrgMember,
  useUnsuspendOrgMember
} from '@/api/mutations/useOrgMembers';
import { orgMemberKeys } from '@/api/query-keys';

const lastSegment = (key: readonly unknown[]) => key[key.length - 1];

const SEND_INVITATION = lastSegment(orgMemberKeys.sendInvitationMutation());
const SUSPEND = lastSegment(orgMemberKeys.suspendMutation());
const UNSUSPEND = lastSegment(orgMemberKeys.unsuspendMutation());

interface OrgMemberStatusActionsOptions {
  /** The member the member dialog shows. */
  dialogMemberId?: string;
  /** Which opening of the member dialog is showing; a reopen is a new one. */
  dialogOpening?: number;
  /** Runs after a member dialog action succeeds, with the opening it started from. */
  onDialogActionDone?: (opening: number) => void;
}

/**
 * Send-invitation, suspend and unsuspend handlers shared by the member tables
 * and their member dialog, plus the ids of the rows with one of them in flight.
 * The row handlers resolve to whether the action succeeded and close nothing:
 * a row action must not close a dialog that shows another member. The dialog
 * gets its own handlers and busy flags in `memberDialogProps`; each reports
 * the dialog opening it started from, so the page closes only that one.
 */
export function useOrgMemberStatusActions({
  dialogMemberId,
  dialogOpening = 0,
  onDialogActionDone
}: OrgMemberStatusActionsOptions = {}) {
  const { t } = useTranslation();
  const { mutateAsync: sendInvitation } = useSendOrgMemberInvitation();
  const { mutateAsync: suspend } = useSuspendOrgMember();
  const { mutateAsync: unsuspend } = useUnsuspendOrgMember();
  // Read from the mutation cache rather than the hooks' `variables`, which only
  // remember the latest call: a second row's action must not clear the first's.
  const pendingActions = useMutationState({
    filters: { mutationKey: orgMemberKeys.rowActionMutations(), status: 'pending' },
    select: (mutation) => ({
      action: mutation.options.mutationKey && lastSegment(mutation.options.mutationKey),
      memberId: String(mutation.state.variables)
    })
  });
  const pendingMemberIds = useMemo(
    () => new Set(pendingActions.map(({ memberId }) => memberId)),
    [pendingActions]
  );

  const createStatusActionHandler = useCallback(
    (mutateAsync: (memberId: string) => Promise<unknown>, actionName: string, successKey: string) =>
      async (memberId: string): Promise<boolean> => {
        try {
          await mutateAsync(memberId);
          toast.success(t(successKey));
          return true;
        } catch (error) {
          console.error(`Failed to ${actionName}:`, error);
          toastRequestError(error, translateErrorMessage(error as ApiError, t));
          return false;
        }
      },
    [t]
  );

  const handleSendInvitation = useMemo(
    () =>
      createStatusActionHandler(
        sendInvitation,
        'send invitation',
        'organization.members.sendInvitationSuccess'
      ),
    [createStatusActionHandler, sendInvitation]
  );

  const handleSuspend = useMemo(
    () =>
      createStatusActionHandler(suspend, 'suspend member', 'organization.members.suspendSuccess'),
    [createStatusActionHandler, suspend]
  );

  const handleUnsuspend = useMemo(
    () =>
      createStatusActionHandler(
        unsuspend,
        'unsuspend member',
        'organization.members.unsuspendSuccess'
      ),
    [createStatusActionHandler, unsuspend]
  );

  const memberDialogProps = useMemo(() => {
    const closingOnSuccess =
      (handler: (memberId: string) => Promise<boolean>) => async (memberId: string) => {
        if (await handler(memberId)) onDialogActionDone?.(dialogOpening);
      };
    const isDialogMemberPending = (action: unknown) =>
      pendingActions.some(
        (pending) => pending.memberId === dialogMemberId && pending.action === action
      );
    return {
      onSendInvitation: closingOnSuccess(handleSendInvitation),
      onSuspend: closingOnSuccess(handleSuspend),
      onUnsuspend: closingOnSuccess(handleUnsuspend),
      isSendingInvitation: isDialogMemberPending(SEND_INVITATION),
      isSuspending: isDialogMemberPending(SUSPEND),
      isUnsuspending: isDialogMemberPending(UNSUSPEND)
    };
  }, [
    handleSendInvitation,
    handleSuspend,
    handleUnsuspend,
    onDialogActionDone,
    pendingActions,
    dialogMemberId,
    dialogOpening
  ]);

  return {
    handleSendInvitation,
    handleSuspend,
    handleUnsuspend,
    pendingMemberIds,
    memberDialogProps
  };
}
